/* Phase 4 §6 — hidden textarea: the OS text-input surface.
 *
 * The textarea owns IME composition, keyboard repeat, and OS shortcuts. It is
 * positioned at the caret (so IME popups anchor correctly), kept invisible
 * (opacity 0.01 — not 0; Safari drops events on opacity:0) and click-through
 * (pointer-events:none). `beforeinput` is captured and mapped to engine
 * commands; the textarea itself never accumulates text. */
import { createEffect, onCleanup, onMount } from 'solid-js';
import type { EngineClient } from '../engine/engine-client';
import type { Command, FormattingToggle, MoveDirection } from '../engine/types';
import type { EngineStore } from '../state/engine-store';
import {
    clearSelectedImage,
    editingStoryForPointer,
    fieldCodeViewForKeys,
    selectedImageForKeys,
} from '../state/engine-store';
import { isModifierOnly, planImageKey, runImageKey } from '../input/image-keys';
import { ClipboardWriteError, copy, cut, paste, writeSync } from '../input/clipboard';
import { createClipboardPrefetch } from '../input/clipboard-cache';
import { devHooksEnabled, installDevHook } from '../dev-hooks';

/** Map a non-composition `InputEvent` to an engine command. */
function mapInputEventToCommand(e: InputEvent): Command | null {
    switch (e.inputType) {
        case 'insertText':
            /* Issue #53 — `at: undefined` = "at the engine's live
               caret". The UI-side mirror only updates when
               SELECTION_CHANGED lands, so a keystroke racing a click's
               caret placement would otherwise carry the previous
               position into the queue. */
            return e.data ? { type: 'INSERT_TEXT', at: undefined, text: e.data } : null;
        /* A <textarea> fires `insertLineBreak` for Enter; Shift+Enter is
           intercepted earlier in `onKeyDown` (soft break) and never reaches
           here. `insertParagraph` is contenteditable semantics. The document
           model has only paragraphs, so a hard Enter splits the block. */
        case 'insertParagraph':
        case 'insertLineBreak':
            /* Issue #64 — split at the engine's live caret, same
               reasoning as INSERT_TEXT above. */
            return { type: 'SPLIT_PARAGRAPH', at: undefined };
        case 'deleteContentBackward':
            return { type: 'DELETE_AT_CARET', forward: false, by_word: false };
        case 'deleteContentForward':
            return { type: 'DELETE_AT_CARET', forward: true, by_word: false };
        case 'deleteWordBackward':
            return { type: 'DELETE_AT_CARET', forward: false, by_word: true };
        case 'deleteWordForward':
            return { type: 'DELETE_AT_CARET', forward: true, by_word: true };
        default:
            /* insertFromPaste → clipboard handler (D4.9); others ignored. */
            return null;
    }
}

export function HiddenInput(props: { client: EngineClient; store: EngineStore }) {
    let ref: HTMLTextAreaElement | undefined;

    /* Track the textarea to the caret so OS IME popups anchor at the caret. */
    createEffect(() => {
        const c = props.store.caret();
        if (ref && c) {
            ref.style.left = `${c.x}px`;
            ref.style.top = `${c.y}px`;
            ref.style.height = `${c.h}px`;
        }
    });

    /* Issue #510 — `preventScroll`: the textarea sits at the CARET, so a
       plain `focus()` scrolled `.editor-viewport` back to the caret on
       every refocus. A double-click in the footer band of a page scrolled
       to its bottom lost its second half that way: the first press was
       swallowed by the band gate, its pointerup re-grabbed focus and
       jumped the view to the caret at the top of the page, and the second
       press + `dblclick` landed on the body under the pointer (a word
       selection instead of entering the footer). Keeping the caret in
       view is the caret reveal's job (`CaretReveal`, issue #387), which
       knows when the user asked for it. */
    const focus = (): void => ref?.focus({ preventScroll: true });

    /* Focus stewardship (issues #35/#49). A canvas click blurs focus to
       <body> via the mousedown default, so the textarea re-grabs it on
       pointerup — but it must never steal focus from a control the user
       is actually interacting with. Skip the re-grab when the pointerup
       lands on (or inside) a focus-owning surface: form controls, their
       labels, editable regions, and open dialogs/popovers. Plain buttons
       are deliberately NOT exempt — after a toolbar button click (Bold,
       alignment, …) typing must continue into the document, which is
       exactly the re-grab; buttons inside dialogs are covered by the
       [role="dialog"] ancestor test. */
    const FOCUS_SINK =
        'input, textarea, select, option, label, ' +
        '[contenteditable="true"], [role="dialog"], .nge-dialog-overlay';
    const onPointerUp = (e: PointerEvent): void => {
        const t = e.target;
        if (t instanceof Element && t.closest(FOCUS_SINK)) return;
        focus();
    };
    /* On window re-focus the browser restores the pre-blur activeElement
       (possibly a dialog field) — only re-grab when nothing else holds
       focus, otherwise the restore is immediately undone. */
    const onWindowFocus = (): void => {
        const a = document.activeElement;
        if (!a || a === document.body || a === ref) focus();
    };
    onMount(() => {
        focus();
        document.addEventListener('pointerup', onPointerUp);
        window.addEventListener('focus', onWindowFocus);
    });
    onCleanup(() => {
        document.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('focus', onWindowFocus);
    });

    const onCompositionStart = (): void => {
        /* Issue #64 — anchor at the engine's live caret (`at:
           undefined`); the UI mirror may still hold the pre-click
           position. The mirror is only a readiness gate here. */
        if (props.store.caretLogical()) {
            void props.client.dispatch({ type: 'BEGIN_COMPOSITION', at: undefined });
        }
    };
    const onCompositionUpdate = (e: CompositionEvent): void => {
        void props.client.dispatch({
            type: 'UPDATE_COMPOSITION',
            text: e.data,
            target_range: undefined,
        });
    };
    const onCompositionEnd = (): void => {
        void props.client.dispatch({ type: 'END_COMPOSITION', commit: true });
        if (ref) ref.value = '';
    };

    const onBeforeInput = (e: InputEvent): void => {
        if (e.isComposing) return; /* the composition handlers own this */
        e.preventDefault();
        /* The mirror caret is only a readiness gate (no selection yet
           ⇒ nothing to edit); every mapped command is caret-relative and
           resolves against the engine's LIVE selection (#53/#64). */
        if (props.store.caretLogical()) {
            const cmd = mapInputEventToCommand(e);
            if (cmd) void props.client.dispatch(cmd);
        }
        if (ref) ref.value = '';
    };

    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.isComposing) return;

        /* Issue #44 — any real keyboard interaction (typing, navigation,
           deletion) means the user is working with the text caret, so
           deselect any inline image whose resize handles are showing.
           Bare modifier presses (Shift/Ctrl/Alt/Meta) are skipped so a
           Shift-in-preparation doesn't drop the selection.

           Issue #554 — object selection REPLACES the caret, so the key is
           first resolved against the picture: Delete / Backspace remove it,
           arrows collapse the selection to a caret at its edge (Up / Down
           then walk from the picture's own line), Escape / typing / Enter
           put the caret before it (see `input/image-keys.ts`). The commands
           are posted synchronously, ahead of any `beforeinput` this key
           causes, so the worker applies them in key order. */
        if (!isModifierOnly(e.key)) {
            const img = selectedImageForKeys();
            const story = editingStoryForPointer();
            const outcome = img
                ? runImageKey(
                      planImageKey(e, img),
                      img,
                      props.client,
                      story && { area: story.area, rid: story.rid },
                      (what, err) => console.error(`${what} failed`, err),
                  )
                : 'continue';
            clearSelectedImage();
            if (outcome === 'consumed') {
                e.preventDefault();
                return;
            }
        }

        /* Phase 3 (#39) — Esc leaves header/footer editing (Word
           parity). Lives HERE, not on a second window-level listener:
           HiddenInput already owns every editing-adjacent key, and
           Dialog.tsx's own Escape handler doesn't stopPropagation — a
           global listener would double-fire when a dialog closes while
           a story is active. */
        if (e.key === 'Escape' && editingStoryForPointer()) {
            e.preventDefault();
            void props.client.dispatch({ type: 'EXIT_HEADER_FOOTER' });
            return;
        }

        /* Issue #77 — F9 updates every field; Alt+F9 toggles the
           field-code view (Word parity). Both are engine state: the
           toggle flips the last broadcast flag, and the reply's
           `SelectionChanged.field_code_view` re-syncs every mirror. */
        if (e.key === 'F9' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            e.preventDefault();
            if (e.altKey) {
                void props.client.dispatch({
                    type: 'SET_FIELD_CODE_VIEW',
                    enabled: !fieldCodeViewForKeys(),
                });
            } else {
                void props.client.dispatch({ type: 'UPDATE_FIELDS' });
            }
            return;
        }

        /* Arrow keys: caret motion. Shift extends the selection
           (anchor stays put); Ctrl/Cmd promotes ArrowLeft/Right to
           word-jump (`WordLeft`/`WordRight`). Up/Down ignore the
           Ctrl/Cmd modifier — no paragraph-jump shortcut at this
           tier. preventDefault stops the textarea from scrolling
           its own (empty) viewport. */
        const wordJump = (e.ctrlKey || e.metaKey) && !e.altKey;
        const arrow: MoveDirection | undefined =
            e.key === 'ArrowUp'
                ? 'Up'
                : e.key === 'ArrowDown'
                  ? 'Down'
                  : e.key === 'ArrowLeft'
                    ? wordJump
                        ? 'WordLeft'
                        : 'Left'
                    : e.key === 'ArrowRight'
                      ? wordJump
                          ? 'WordRight'
                          : 'Right'
                      : undefined;
        if (arrow) {
            e.preventDefault();
            void props.client.dispatch({
                type: 'MOVE_CARET',
                direction: arrow,
                extend: e.shiftKey,
            });
            return;
        }

        /* Home / End. Ctrl/Cmd promotes line-bounded motion to document
           bounds (`DocHome` / `DocEnd`). Shift extends as with arrows.
           Issue #462 — PageDown / PageUp move one viewport height; the
           engine lays out past the lazy band itself before resolving any
           of these, so a burst never stops at the band's edge. */
        const docJump = (e.ctrlKey || e.metaKey) && !e.altKey;
        const lineMotion: MoveDirection | undefined =
            e.key === 'Home'
                ? docJump
                    ? 'DocHome'
                    : 'LineHome'
                : e.key === 'End'
                  ? docJump
                      ? 'DocEnd'
                      : 'LineEnd'
                  : e.key === 'PageDown' && !docJump
                    ? 'PageDown'
                    : e.key === 'PageUp' && !docJump
                      ? 'PageUp'
                      : undefined;
        if (lineMotion) {
            e.preventDefault();
            void props.client.dispatch({
                type: 'MOVE_CARET',
                direction: lineMotion,
                extend: e.shiftKey,
            });
            return;
        }

        /* Tab — context-sensitive (Ctrl+Tab still falls through to the
           textarea default). Inside a table cell it navigates cell-to-cell
           (Shift+Tab = previous). Inside a list item it demotes/promotes
           the outline level (Issue #42). Otherwise, in body text, it
           inserts a real `\t` (U+0009), which the layout engine then
           aligns to the paragraph's ruler tab stops (or the default
           0.5-inch grid). preventDefault stops the browser stealing Tab
           for focus navigation. */
        if (e.key === 'Tab' && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            const caret = props.store.caretLogical();
            const inCell = !!caret && caret.path.steps.some((s) => s.kind === 'CELL');
            const ilvl = props.store.listIlvl();
            if (inCell) {
                void props.client.dispatch({
                    type: 'MOVE_CARET',
                    direction: e.shiftKey ? 'PrevCell' : 'NextCell',
                    extend: false,
                });
            } else if (caret && ilvl !== undefined) {
                /* Caret sits in a list item — demote (Tab) or promote
                   (Shift+Tab) its outline level instead of inserting a
                   tab character or no-op'ing. Issue #53 — engine-live
                   caret, not the async UI mirror. */
                void props.client.dispatch({
                    type: 'CHANGE_LIST_LEVEL',
                    range: { start: caret, end: caret },
                    delta: e.shiftKey ? -1 : 1,
                });
            } else if (caret && !e.shiftKey) {
                /* Body paragraph → insert a tab character. Shift+Tab in body
                   has no outdent command yet, so it is a no-op (it still
                   preventDefaults to avoid focus loss). Issue #53 —
                   engine-live caret, not the async UI mirror. */
                void props.client.dispatch({
                    type: 'INSERT_TEXT',
                    at: undefined,
                    text: '\t',
                });
            }
            return;
        }

        /* Shift+Enter — SOFT break. A <textarea> fires `insertLineBreak`
           for both Enter and Shift+Enter, so the two are indistinguishable
           at `beforeinput`; catch the soft break here (where `shiftKey` is
           visible), insert a U+2028 LINE SEPARATOR into the run (no
           paragraph split — the layout engine forces a new line on it),
           and preventDefault so the textarea's `insertLineBreak` does NOT
           also fire and split the paragraph. Plain Enter falls through to
           `onBeforeInput` → SPLIT_PARAGRAPH. Modifier+Enter (Ctrl/Alt) is
           left to its existing path. */
        if (
            e.key === 'Enter' &&
            e.shiftKey &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey
        ) {
            e.preventDefault();
            const caret = props.store.caretLogical();
            if (caret) {
                /* Issue #53 — engine-live caret, not the async mirror. */
                void props.client.dispatch({
                    type: 'INSERT_TEXT',
                    at: undefined,
                    text: '\u2028',
                });
            }
            return;
        }

        const mod = e.ctrlKey || e.metaKey;
        if (!mod) return;
        /* Match e.code (physical key) — e.key is layout-dependent, so an
           Arabic layout reports a non-Latin key at the Z position. */
        if (e.code === 'KeyA' && !e.shiftKey) {
            e.preventDefault();
            void props.client.dispatch({ type: 'SELECT_ALL' });
        } else if (e.code === 'KeyZ' && !e.shiftKey) {
            e.preventDefault();
            void props.client.dispatch({ type: 'UNDO' });
        } else if (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey)) {
            e.preventDefault();
            void props.client.dispatch({ type: 'REDO' });
        } else if (e.code === 'KeyV' && e.shiftKey) {
            /* Ctrl/Cmd+Shift+V — Paste Special: Unformatted Text.
               Bypass the HTML path so the engine sees only plain
               text (UX_BEHAVIOR_SPEC §V.2). The default Ctrl+V is
               handled by the `onPaste` handler below via the
               native `paste` ClipboardEvent. */
            e.preventDefault();
            void paste(props.client, true);
        } else if (e.code === 'KeyB' && !e.shiftKey) {
            e.preventDefault();
            toggleFormat('Bold');
        } else if (e.code === 'KeyI' && !e.shiftKey) {
            e.preventDefault();
            toggleFormat('Italic');
        } else if (e.code === 'KeyU' && !e.shiftKey) {
            e.preventDefault();
            toggleFormat('Underline');
        }
    };

    /* Ctrl/Cmd+B/I/U — issue #286: posted synchronously with NO state
       read. The engine derives the target from its own style at the live
       caret (+ any armed pending style); the mirrored `attrsAtCaret()`
       lags the reply at typing speed, so toggling against it lost the
       second Ctrl+B of "Ctrl+B, type, Ctrl+B, type". */
    const toggleFormat = (attr: FormattingToggle): void => {
        void props.client.dispatch({ type: 'TOGGLE_FORMATTING', attr });
    };

    /* §12 — clipboard. The native copy/cut/paste events fire inside a
       user-gesture context, which the async navigator.clipboard API requires;
       preventDefault stops the textarea acting on its own (empty) content.

       Issue #48 — copy/cut can reject (`ClipboardWriteError`) when the
       browser blocks every write tier; the rejection MUST be observed
       and surfaced, never `void`-discarded. On a failed cut nothing was
       deleted (write-before-delete invariant in `input/clipboard.ts`). */
    /* Issue #57 — warm payload for the live selection, so copy/cut can
       write synchronously inside the trusted event. `?clipboardPrefetch=0`
       disables the prefetch (every copy then takes the async path). */
    /* Issue #389 - the kill switch is a build constant
       (`VITE_NGE_CLIPBOARD_PREFETCH=0`); the `?clipboardPrefetch=0` URL
       parameter is honoured only under the dev-hooks flag. */
    const prefetchEnabled =
        import.meta.env.VITE_NGE_CLIPBOARD_PREFETCH !== '0' &&
        !(
            devHooksEnabled() &&
            new URLSearchParams(window.location.search).get('clipboardPrefetch') === '0'
        );
    const prefetch = createClipboardPrefetch(props.client, props.store, prefetchEnabled);
    installDevHook('__clipboardPrefetch', prefetch);

    const onCopy = (e: ClipboardEvent): void => {
        e.preventDefault();
        /* Issue #57 — cache hit: setData synchronously, no await, no
           focus dependency. A miss falls through to the #48 async path
           unchanged. */
        const hit = prefetch.take();
        if (hit && (hit.plain === '' || writeSync(e, hit))) return;
        copy(props.client).catch((err: unknown) => {
            /* Only a ClipboardWriteError means the BROWSER blocked the
               write — anything else is an engine/dispatch failure and
               must not be blamed on clipboard permissions. */
            props.store.setUiError(
                err instanceof ClipboardWriteError
                    ? 'Copy failed — the browser blocked clipboard access. ' +
                          'Click the document and try again.'
                    : 'Copy failed — the editor could not read the selection.',
            );
        });
    };
    const onCut = (e: ClipboardEvent): void => {
        e.preventDefault();
        /* Issue #57 — cache hit: the synchronous write already succeeded
           when `writeSync` returns, so the delete may follow
           (write-before-delete holds). The cache is only trusted with no
           write in flight, so DELETE_AT_CARET removes exactly the range
           that was just written. */
        const hit = prefetch.take();
        if (hit && hit.plain === '') return;
        if (hit && writeSync(e, hit)) {
            props.client
                .dispatch({ type: 'DELETE_AT_CARET', forward: false, by_word: false })
                .catch(() => {
                    props.store.setUiError('Cut failed — an editor error occurred.');
                });
            return;
        }
        cut(props.client).catch((err: unknown) => {
            /* ClipboardWriteError ⇒ the write was blocked and the delete
               was skipped (write-before-delete), so "nothing was deleted"
               is a guarantee. Any other rejection may have come from the
               DELETE dispatch AFTER a successful write — stay vague. */
            props.store.setUiError(
                err instanceof ClipboardWriteError
                    ? 'Cut failed — the browser blocked clipboard access. ' +
                          'Nothing was deleted; click the document and try again.'
                    : 'Cut failed — an editor error occurred.',
            );
        });
    };
    const onPaste = (e: ClipboardEvent): void => {
        e.preventDefault();
        void paste(props.client);
    };

    return (
        <textarea
            ref={ref}
            class="hidden-input"
            /* `aria-hidden` removed — browsers warn (and the WAI-ARIA spec
               forbids) hiding a focused element from AT. The textarea
               holds the OS text-input focus continuously; the canonical
               AT surface is the visually-hidden `AccessibilityTree`
               component rendered separately, not this one-shot input
               buffer (cleared on every keystroke). `aria-label` gives
               AT a one-word identifier instead of "edit, blank". */
            aria-label="editor input"
            /* Focus hand-back hook — `@nge/ui`'s focusEditorInput()
               (Ruler drag-end, Dialog close, comment-draft close) targets
               `textarea[data-nge-hidden-input]` to return typing focus to
               the document. The SDK's EditorSurface exposes the same hook
               as `#nge-hidden-input`. */
            data-nge-hidden-input=""
            /* Issue #389 - passive, observable state (not a capability). */
            data-clipboard-prefetch={prefetchEnabled ? 'on' : 'off'}
            tabindex="-1"
            autocomplete="off"
            autocapitalize="off"
            spellcheck={false}
            onCompositionStart={onCompositionStart}
            onCompositionUpdate={onCompositionUpdate}
            onCompositionEnd={onCompositionEnd}
            onBeforeInput={onBeforeInput}
            onKeyDown={onKeyDown}
            onCopy={onCopy}
            onCut={onCut}
            onPaste={onPaste}
        />
    );
}
